import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  Tooltip,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import { statementSourceTagService } from '../../services/api/statementSourceTagService';

const ResizableTableCell = ({ children, width, onResize, ...props }) => (
  <TableCell
    {...props}
    sx={{
      width: width,
      minWidth: width,
      maxWidth: width,
      position: 'relative',
      padding: '8px',
      ...props.sx
    }}
  >
    {children}
    {onResize && (
      <Box
        onMouseDown={onResize}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '5px',
          cursor: 'col-resize',
          userSelect: 'none',
          '&:hover': {
            backgroundColor: 'primary.main',
            opacity: 0.3
          }
        }}
      />
    )}
  </TableCell>
);

const SourceTagManager = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [editFormData, setEditFormData] = useState({ label: '', description: '' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [columnWidths, setColumnWidths] = useState({
    tagId: 180,
    label: 150,
    description: 200,
    source: 90,
    type: 80,
    category: 100,
    dataSource: 110,
    usage: 70
  });
  const [resizing, setResizing] = useState(null);

  useEffect(() => {
    loadTags();
  }, [categoryFilter]);

  const loadTags = async () => {
    try {
      setLoading(true);
      setError(null);
      const filters = categoryFilter ? { category: categoryFilter } : {};
      const response = await statementSourceTagService.listSourceTags(filters);
      setTags(response.tags || []);
    } catch (err) {
      console.error('Error loading tags:', err);
      setError(err.message || 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const getSourceColor = (source) => {
    switch (source) {
      case 'system':
        return 'primary';
      case 'witness':
        return 'success';
      case 'computed':
        return 'warning';
      case 'reviewer':
        return 'info';
      default:
        return 'default';
    }
  };

  const getDataTypeColor = (dataType) => {
    switch (dataType) {
      case 'string':
        return 'default';
      case 'number':
        return 'primary';
      case 'boolean':
        return 'success';
      case 'date':
        return 'info';
      case 'array':
        return 'warning';
      case 'object':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleEditClick = (tag) => {
    setEditingTag(tag);
    setEditFormData({
      label: tag.label,
      description: tag.description || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingTag || !editFormData.label) {
      setError('Label is required');
      return;
    }

    try {
      await statementSourceTagService.updateSourceTag(
        editingTag.tag_id,
        {
          label: editFormData.label,
          description: editFormData.description
        }
      );
      await loadTags();
      setEditDialogOpen(false);
      setEditingTag(null);
      setEditFormData({ label: '', description: '' });
    } catch (err) {
      console.error('Error updating tag:', err);
      setError(err.message || 'Failed to update tag');
    }
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setEditingTag(null);
    setEditFormData({ label: '', description: '' });
  };

  const handleMouseDown = (columnKey) => (e) => {
    setResizing({ columnKey, startX: e.clientX, startWidth: columnWidths[columnKey] });
  };

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const delta = e.clientX - resizing.startX;
      const newWidth = Math.max(60, resizing.startWidth + delta);
      setColumnWidths(prev => ({ ...prev, [resizing.columnKey]: newWidth }));
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing]);

  const filteredTags = tags.filter((tag) => {
    const matchesSearch =
      searchTerm === '' ||
      tag.tag_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tag.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tag.description && tag.description.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesSearch;
  });

  // Paginate filtered tags
  const paginatedTags = filteredTags.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Get unique categories for filter chips
  const categories = [...new Set(tags.map(tag => tag.category).filter(Boolean))];

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const parseDataSource = (dataSourceStr) => {
    try {
      return JSON.parse(dataSourceStr);
    } catch {
      return null;
    }
  };

  const renderDataSource = (tag) => {
    const dataSource = parseDataSource(tag.data_source);
    if (!dataSource) return '-';

    switch (dataSource.type) {
      case 'dynamodb':
        return (
          <Tooltip title={`Table: ${dataSource.table}, Field: ${dataSource.value_field}`}>
            <span>DynamoDB</span>
          </Tooltip>
        );
      case 'user_input':
        return 'User Input';
      case 'computed':
        return (
          <Tooltip title={`Formula: ${dataSource.formula}`}>
            <span>Computed</span>
          </Tooltip>
        );
      case 'context':
        return (
          <Tooltip title={`Context path: ${dataSource.path}`}>
            <span>Context</span>
          </Tooltip>
        );
      default:
        return dataSource.type;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Typography variant="h6">
          Tag Registry
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>{filteredTags.length}</strong> tags
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, flexWrap: 'nowrap', overflowX: 'auto' }}>
        <TextField
          size="small"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 300, flexShrink: 0 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Chip
          label="All"
          onClick={() => setCategoryFilter(null)}
          color={categoryFilter === null ? 'primary' : 'default'}
          size="small"
        />
        {categories.map((category) => (
          <Chip
            key={category}
            label={category}
            onClick={() => setCategoryFilter(category)}
            color={categoryFilter === category ? 'primary' : 'default'}
            size="small"
          />
        ))}
      </Box>

      <Paper sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <ResizableTableCell width={columnWidths.tagId} onResize={handleMouseDown('tagId')}>
                  Tag ID
                </ResizableTableCell>
                <ResizableTableCell width={columnWidths.label} onResize={handleMouseDown('label')}>
                  Label
                </ResizableTableCell>
                <ResizableTableCell width={columnWidths.description} onResize={handleMouseDown('description')}>
                  Description
                </ResizableTableCell>
                <ResizableTableCell width={columnWidths.source} onResize={handleMouseDown('source')}>
                  Source
                </ResizableTableCell>
                <ResizableTableCell width={columnWidths.type} onResize={handleMouseDown('type')}>
                  Type
                </ResizableTableCell>
                <ResizableTableCell width={columnWidths.category} onResize={handleMouseDown('category')}>
                  Category
                </ResizableTableCell>
                <ResizableTableCell width={columnWidths.dataSource} onResize={handleMouseDown('dataSource')}>
                  Data Source
                </ResizableTableCell>
                <ResizableTableCell width={columnWidths.usage} onResize={handleMouseDown('usage')} align="center">
                  Usage
                </ResizableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedTags.length === 0 ? (
                <TableRow>
                  <ResizableTableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No tags found
                    </Typography>
                  </ResizableTableCell>
                </TableRow>
              ) : (
                paginatedTags.map((tag) => (
                  <TableRow
                    key={tag.tag_id}
                    hover
                    onClick={() => handleEditClick(tag)}
                    sx={{
                      cursor: 'pointer',
                      '& td': { py: 0.75 },
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    <ResizableTableCell width={columnWidths.tagId}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        {tag.tag_id}
                      </Typography>
                    </ResizableTableCell>
                    <ResizableTableCell width={columnWidths.label}>
                      <Typography variant="body2" fontWeight="500">
                        {tag.label}
                      </Typography>
                    </ResizableTableCell>
                    <ResizableTableCell width={columnWidths.description}>
                      {tag.description ? (
                        <Tooltip title={tag.description}>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ cursor: 'help' }}>
                            {tag.description}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.disabled">-</Typography>
                      )}
                    </ResizableTableCell>
                    <ResizableTableCell width={columnWidths.source}>
                      <Chip
                        label={tag.source}
                        color={getSourceColor(tag.source)}
                        size="small"
                      />
                    </ResizableTableCell>
                    <ResizableTableCell width={columnWidths.type}>
                      <Chip
                        label={tag.data_type}
                        color={getDataTypeColor(tag.data_type)}
                        size="small"
                        variant="outlined"
                      />
                    </ResizableTableCell>
                    <ResizableTableCell width={columnWidths.category}>
                      <Typography variant="body2" color="text.secondary">
                        {tag.category || '-'}
                      </Typography>
                    </ResizableTableCell>
                    <ResizableTableCell width={columnWidths.dataSource}>
                      <Typography variant="body2" color="text.secondary">
                        {renderDataSource(tag)}
                      </Typography>
                    </ResizableTableCell>
                    <ResizableTableCell width={columnWidths.usage} align="center">
                      <Chip
                        label={tag.usage_count || 0}
                        size="small"
                        variant="outlined"
                      />
                    </ResizableTableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredTags.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider' }}
        />
      </Paper>

      {/* Edit Tag Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Tag Label</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tag ID: <strong>{editingTag?.tag_id}</strong>
          </Typography>

          <TextField
            fullWidth
            label="Label"
            value={editFormData.label}
            onChange={(e) => setEditFormData({ ...editFormData, label: e.target.value })}
            margin="normal"
            required
            helperText="Display label for this tag"
          />

          <TextField
            fullWidth
            label="Description"
            value={editFormData.description}
            onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
            helperText="Optional description of what this tag represents"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel}>Cancel</Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={!editFormData.label}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(SourceTagManager);
